import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import {CustomSearch, PublicCustomSearch, SERVICE_ADVANCED_SEARCH} from '../reduxToolkit/Commons/CommonsApi';
import { CustomAddChip } from './CustomAddChip';
import {TABLES} from '../Apis/Tables';

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle: Full Stack Developer - IBL
 * @Email: clopez@ibl.mx
 *
 * @param {string} autocompleteId - Unique ID for the autocomplete input.
 * @param {string} buttonId - Unique ID for the add button.
 * @param {string} label - Key to be translated as the input label.
 * @param {Array<{ id: string, label: string }>} selectedList - Items currently selected and rendered as chips.
 * @param {function} setSelectedList - Callback to update the selected items list.
 * @param userTypeIdsSearch - Array of user type IDs used to filter search results.
 * @param {boolean} [required] - Whether the field is required.
 * @param {boolean} [showRenderOption=false] - Enables custom rendering for options in the dropdown.
 * @param {function} [filterOptions=filterByLabelEmailPhone] - Custom filtering function for autocomplete options.
 * @param {boolean} [hideButton=false] - If true, hides the "Add" button and automatically adds items on selection
 * @param isFetchOnSearch - If true, triggers the API call while typing in the input.
 * @param {boolean} [disabled] - Disables the autocomplete, button, and chips if true.
 * @param {String} api - Service to search
 * @param {model} bodyApi - model to search
 * @param {number} rowsPerPage - number for total items
 * @param {String} model - model indicates to table option
 * @param {boolean} [emptyFields] - Whether the input should be flagged as empty (validation).
 * @param {ReactNode} [noOptionsCard] - Optional card to show when there are no results.
 * @param {number|string} [maxLength=undefined] - Maximum number of items allowed in `selectedList`.
 * @param {function} reset - Callback used to reset the autocomplete and chip list.
 * @param {string|number} keyId - Field used as the unique identifier for each option returned from the API.
 * @param {boolean} showSubSection - Enables rendering additional subgroup information inside the option label.
 * @param {string} helperText - Helper message displayed below the input.
 * @param {boolean} onlyOnKeyDown - If true, triggers the search only on keyDown instead of onChange.
 * @param {boolean} completeWidth - Expands the component to use the full available width.
 * @param classNameCustomCards - New class for a box containing custom medium chips
 * @param {boolean} [completeWidth=false] - If true, chip stretches to full container width.
 * @param sanitizeDataList=userListToLabelEmailPhoneValue - A callback responsible for mapping API list results into a standardized structure.It is typically a function like `userListToLabelEmailPhoneValue`.
 * @param Icon: Icon component (optional)
 * @param {React.ElementType} [chipIcon=PersonChipBlack] - Custom icon component for each chip.
 * @param String placeholder: Defines the placeholder text for the input field (optional)
 */
const CustomAddChipSearch = (
    {
        autocompleteId = 'slt_cacs_search',
        buttonId = 'btn_cacs_search',
        label = 'search',
        selectedList = [], // required
        setSelectedList, // required
        userTypeIdsSearch = [], // required
        required = false,
        showRenderOption = true,
        hideButton = true,
        isFetchOnSearch = true,
        disabled = false,
        api = SERVICE_ADVANCED_SEARCH, // required
        bodyApi = null,
        rowsPerPage = 20,
        model=TABLES.USER_BY_USER_TYPE,
        emptyFields,
        noOptionsCard = null,
        maxLength,
        reset,
        keyId,
        showSubSection,
        helperText='',
        onlyOnKeyDown=false,
        completeWidth=false,
        classNameCustomCards='',
        sanitizeDataList,
        Icon,
        chipIcon=null,
        placeholder,
        cleanChipSearch,
        setCleanChipSearch,
        positionIconOptions,
    }
) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [options, setOptions] = useState([]);
    const [open, setOpen] = useState(false);

    const handleSearch = (search) => {
        if (search.trim()) {
            handleApiSearch(search, userTypeIdsSearch);
        } else {
            setOptions([]);
            setOpen(false);
        }
    };

    const handleApiSearch = (search, userTypeIds) => {
        let body;
        if(bodyApi === null){
            body = {
                key: search,
                pagination: {
                    page: 0,
                    size: rowsPerPage,
                },
                userTypeIds: userTypeIds,
                model:model
            };
        }else{
            body= { ...bodyApi, key: search };
        }

        const searchAction = api.includes("public/")
            ? PublicCustomSearch
            : CustomSearch;

        dispatch(searchAction({ body, api }))
            .then(({ payload: { data: { data } } }) => {
                const content = data?.content ??
                                    (Array.isArray(data) && data.length > 0
                                        ? data
                                        : []);

                setOptions(sanitizeDataList(content, keyId, showSubSection, Icon));
                setOpen(true);
            });
    }

    return (
        <CustomAddChip
            autocompleteId={autocompleteId}
            buttonId={buttonId}
            label={t(label)}
            required={required}
            data={options}
            selectedList={selectedList}
            setSelectedList={setSelectedList}
            showRenderOption={showRenderOption}
            maxLength={maxLength}
            hideButton={hideButton}
            open={open}
            setOpen={setOpen}
            searchApi={(value) => {
                handleSearch(value);
            }}
            isFetchOnSearch={isFetchOnSearch}
            disabled={disabled}
            emptyFields={emptyFields}
            noOptionsCard={noOptionsCard}
            reset={reset}
            helperText={helperText}
            onlyOnKeyDown={onlyOnKeyDown}
            completeWidth={completeWidth}
            classNameCustomCards={classNameCustomCards}
            chipIcon={chipIcon}
            placeholder={placeholder}
            cleanChipSearch={cleanChipSearch}
            setCleanChipSearch={setCleanChipSearch}
            positionIconOptions={positionIconOptions}
        />
    );

};

export default CustomAddChipSearch
